import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BOARDS, COMPETITION_TIERS, COMPETITION_TYPES } from '@/data/content';

export default function HomePage() {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [activeVideo, setActiveVideo] = useState<number | null>(null);

  const handleEnter = (i: number) => {
    const v = videoRefs.current[i];
    if (v) { v.currentTime = 0; v.play(); }
  };
  const handleLeave = (i: number) => {
    const v = videoRefs.current[i];
    if (v) { v.pause(); v.currentTime = 0; }
  };

  return (
    <div >
      {/* ---- Hero ---- */}
      <section className="snap-start min-h-[calc(100vh-80px)] flex items-center relative overflow-hidden px-6 pt-8 pb-16 md:pt-12 md:pb-24">
        {/* Video background */}
        <div className="absolute inset-0 z-0">
          <img src="/images/hero_product.webp" alt="" className="absolute inset-0 w-full h-full object-cover md:hidden" />
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/images/hero_product.webp"
            className="absolute inset-0 w-full h-full object-cover hidden md:block"
          >
            <source src="/videos/hero-bg.webm" type="video/webm" />
<source src="/videos/hero-bg.mp4" type="video/mp4" />
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto w-full text-center">
          <h1 className="text-hero font-extrabold mb-4 text-white drop-shadow-[0_0_30px_rgba(0,0,0,0.8)] sm:whitespace-nowrap">从第一颗螺丝到无限可能</h1>
          <p className="text-white/90 mb-2 text-lg drop-shadow-lg">拼装 / 改装 / 编程 / 对战</p>
          <p className="text-white/70 mb-8 text-sm max-w-md mx-auto drop-shadow-lg">从第一颗螺丝拧下去的那一刻起，你的战车能走多远——由你决定。</p>
            <div className="flex gap-3 justify-center">
              <Link to="/products" className="btn-secondary text-sm px-6 h-10 inline-flex items-center">探索战车</Link>
              <Link to="/register" className="btn-primary text-sm px-6 h-10 inline-flex items-center">立即加入</Link>
            </div>
            {/* Quick stats */}
            <div className="flex gap-8 mt-10 justify-center">
              {[
                { v: 'XX', l: '零售价', u: '起' },
                { v: '4', l: '主板等级', u: 'A/B/C/S' },
                { v: '48', l: '课时', u: '覆盖6大学科' },
              ].map((s) => (
                <div key={s.l} className="text-center">
                  <p className="text-2xl font-extrabold font-mono text-white">{s.v}<span className="text-sm text-white/70 font-sans ml-0.5">{s.u}</span></p>
                  <p className="text-[11px] text-white/50 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      {/* ---- Boards Overview ---- */}
      <section className="snap-start bg-panel-dark/35 px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-extrabold text-text-primary mb-3">一块底盘，四种大脑</h2>
            <p className="text-text-secondary text-sm max-w-xl mx-auto">换一张主板，就是一辆全新的车。从图形化编程到Linux+ROS，一块底盘承载全年龄段。</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {BOARDS.map((b) => (
              <Link key={b.id} to="/products" className="group relative bg-panel-mid/35 border border-border-dark rounded-card mecha-card mecha-bolt p-5 hover:border-arena-blue/30 transition-all duration-300 hover:scale-105 hover:-translate-y-2 hover:z-20 hover:shadow-[0_8px_40px_rgba(0,0,0,0.5)] text-center">
                <img src={b.image} alt={b.name} className="h-24 w-auto mx-auto mb-4 object-contain transition-transform duration-300 group-hover:scale-110" />
                <p className={`text-lg font-extrabold ${b.color === 'board-c' ? 'text-board-c' : b.color === 'board-b' ? 'text-board-b' : b.color === 'board-a' ? 'text-board-a' : 'text-board-s'}`}>
                  {b.id} 板
                </p>
                <p className="text-xs text-text-secondary mt-1">{b.price}</p>
                <p className="text-[11px] text-text-disabled mt-1">{b.ageGroup}</p>
                <p className="text-[11px] text-text-disabled">{b.programmingLang}</p>
                {/* Hover detail popup */}
                <div className="relative md:absolute md:left-1/2 md:-translate-x-1/2 md:top-full md:mt-2 w-full md:w-56 mt-3 bg-panel-dark border border-arena-blue/30 rounded-card-lg p-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 md:pointer-events-none z-30 shadow-[0_8px_30px_rgba(0,0,0,0.6)] text-left">
                  <p className="text-xs text-text-primary font-semibold mb-1">{b.name}</p>
                  <p className="text-[11px] text-text-secondary leading-relaxed">{b.subtitle}</p>
                  <div className="mt-1.5 pt-1.5 border-t border-border-dark text-[10px] text-arena-blue/80">{b.programmingLang} · {b.ageGroup}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/products" className="text-sm text-arena-blue hover:underline">查看完整参数对比</Link>
          </div>
        </div>
      </section>

      {/* ---- Value Proposition ---- */}
	      <section className="snap-start bg-bg-black/30 px-6 py-12">
	        <div className="max-w-6xl mx-auto text-center">
	          <h2 className="text-2xl font-extrabold text-text-primary mb-4">为什么是 RoboRush</h2>
	          <p className="text-text-secondary text-sm mb-10">玩具遥控车玩三天就腻。平板编程课和现实世界没有连接。我们填补中间的空缺。</p>
	          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
	            {[
	              { t: "低门槛", d: "15分钟免工具拼装，6岁独立完成", v: "/videos/value-low.mp4", p: "/images/value_low.webp", c: "text-board-a" },
	              { t: "高天花板", d: "图形化编程 → Python → Linux+ROS", v: "/videos/value-high.mp4", p: "/images/value_high.webp", c: "text-arena-blue" },
	              { t: "真竞技", d: "碰撞/缩圈/KO·数据大屏实时直播", v: "/videos/value-battle.mp4", p: "/images/value_battle.webp", c: "text-arena-red" },
	            ].map((item, i) => (
	              <div
	                key={item.t}
	                className="relative group cursor-pointer rounded-card-lg overflow-hidden aspect-[9/16] bg-panel-dark border border-border-dark hover:border-arena-blue/40 transition-all duration-500"
	                onMouseEnter={() => handleEnter(i)}
	                onMouseLeave={() => handleLeave(i)}
	                onClick={() => {
	                  const v = videoRefs.current[i];
	                  if (activeVideo === i) {
	                    if (v) { v.pause(); v.currentTime = 0; }
	                    setActiveVideo(null);
	                  } else {
	                    videoRefs.current.forEach((el, j) => { if (el && j !== i) { el.pause(); el.currentTime = 0; } });
	                    if (v) { v.currentTime = 0; v.play(); }
	                    setActiveVideo(i);
	                  }
	                }}
	              >
	                {/* Poster */}
	                <img src={item.p} alt={item.t} className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-300 ${activeVideo === i ? 'opacity-0' : 'group-hover:opacity-0'}`} />
	                {/* Video */}
	                <video
	                  ref={(el) => { videoRefs.current[i] = el; }}
	                  muted
	                  playsInline
	                  preload="none"
	                  className="absolute inset-0 w-full h-full object-cover hidden md:block"
	                  poster={item.p}
	                >
	                  <source src={item.v} type="video/mp4" />
	                </video>
	                {/* Gradient overlay */}
	                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 z-20 pointer-events-none" />
	                {/* Title */}
	                <div className="absolute inset-x-0 top-0 p-5 z-30 pointer-events-none">
	                  <h3 className={`text-xl font-extrabold mb-1 ${item.c}`}>{item.t}</h3>
	                </div>
	                {/* Subtitle */}
	                <div className="absolute inset-x-0 bottom-0 p-5 z-30 pointer-events-none">
	                  <p className="text-xs text-white/70 leading-relaxed">{item.d}</p>
	                </div>
	              </div>
	            ))}
	          </div>
	        </div>
	      </section>
{/* ---- Competition + Rankings ---- */}
      <section className="snap-start bg-panel-dark/35 px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Competition tiers */}
          <div>
            <h2 className="text-xl font-extrabold text-text-primary mb-2">三级赛事体系</h2>
            <p className="text-xs text-text-secondary mb-6">从课间画圈即打到全国总决赛</p>
            <div className="space-y-3 mb-4">
              {COMPETITION_TIERS.map((t, i) => (
                <Link key={t.id} to="/arena" className="flex items-center gap-3 p-3 bg-panel-mid/35 border border-border-dark rounded-card mecha-card mecha-bolt hover:border-arena-blue/40 transition-all group">
                  <span className={`text-xs font-extrabold px-2.5 py-1 rounded-tag shrink-0 ${i === 0 ? 'bg-board-a/15 text-board-a' : i === 1 ? 'bg-arena-blue/15 text-arena-blue' : 'bg-arena-red/15 text-arena-red'}`}>方案 {i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{t.name}</p>
                    <p className="text-xs text-text-secondary">{t.tagline}</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4A5560" strokeWidth="2" className="group-hover:stroke-arena-blue transition-colors shrink-0"><path d="M9 18l6-6-6-6"/></svg>
                </Link>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {COMPETITION_TYPES.map((ct) => (
                <Link key={ct.id} to="/arena" className="flex items-center gap-2 p-2.5 bg-panel-mid/35 border border-border-dark rounded-card mecha-card mecha-bolt hover:border-arena-blue/40 transition-all">
                  <img src={ct.image} alt={ct.title} className="w-12 h-8 object-cover rounded shrink-0" />
                  <div className="min-w-0">
                    <span className="text-[11px] text-text-primary block truncate">{ct.title}</span>
                    <span className={`text-[10px] ${ct.category === '对抗赛' ? 'text-arena-red/70' : 'text-arena-blue/70'}`}>{ct.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Rankings preview + Education */}
          <div className="space-y-6">
            <div>
              <div className="flex items-end justify-between mb-4">
                <h2 className="text-xl font-extrabold text-text-primary">赛季排行</h2>
                <Link to="/rankings" className="text-sm text-arena-blue hover:underline">完整榜单</Link>
              </div>
              <div className="bg-panel-mid/35 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-4 space-y-0.5">
                {[
                  { r: 1, n: '铁犀牛', t: '雷霆战队', s: 9850, w: 92 },
                  { r: 2, n: '闪电侠', t: '疾风战队', s: 9410, w: 88 },
                  { r: 3, n: '磐石', t: '坚盾战队', s: 9120, w: 85 },
                  { r: 4, n: '暗夜猎手', t: '夜鹰战队', s: 8840, w: 82 },
                  { r: 5, n: '烈焰风暴', t: '赤焰战队', s: 8600, w: 80 },
                ].map((e) => (
                  <div key={e.r} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-panel-dark/35 transition-colors">
                    <span className={`w-5 text-right font-mono text-xs font-extrabold ${e.r === 1 ? 'text-arena-gold' : e.r <= 3 ? 'text-text-primary' : 'text-text-secondary'}`}>{e.r}</span>
                    <div className="w-5 h-5 rounded-full bg-border-dark shrink-0" />
                    <span className="text-sm text-text-primary flex-1">{e.n}</span>
                    <span className="text-[11px] text-text-disabled">{e.t}</span>
                    <span className="text-xs font-mono text-text-primary w-14 text-right">{e.s.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-end justify-between mb-4">
                <h2 className="text-xl font-extrabold text-text-primary">从教室到赛场</h2>
                <Link to="/education" className="text-sm text-arena-blue hover:underline">全部课程</Link>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { t: '启蒙课程', age: '6-9岁', n: 16, img: '/images/edu_junior.webp' },
                  { t: '基础课程', age: '10-13岁', n: 16, img: '/images/edu_teen.webp' },
                  { t: '进阶课程', age: '14-16+岁', n: 16, img: '/images/edu_elite.webp' },
                ].map((c) => (
                  <Link key={c.t} to="/education" className="bg-panel-mid/35 border border-border-dark rounded-card mecha-card mecha-bolt p-3 hover:border-arena-blue/40 transition-all group">
                    <img src={c.img} alt={c.t} className="h-16 w-full object-cover rounded mb-2" />
                    <p className="text-sm font-medium text-text-primary">{c.t}</p>
                    <p className="text-[11px] text-text-secondary">{c.age} / {c.n}课时</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Shop Quick Links ---- */}
      <section className="snap-start bg-bg-black/30 px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-xl font-extrabold text-text-primary mb-2">装备库</h2>
              <p className="text-sm text-text-secondary">官方核心件 + 社区创造 = 无限扩展</p>
            </div>
            <Link to="/shop" className="text-sm text-arena-blue hover:underline">进入商城</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            {[
              { n: '公路轮胎', img: '/images/shop_tyre_road.webp', p: 'XX' },
              { n: '越野轮胎', img: '/images/shop_tyre_offroad.webp', p: 'XX' },
              { n: '前铲装甲', img: '/images/shop_armor_front.webp', p: 'XX' },
              { n: '红外传感器', img: '/images/shop_sensor_ir.webp', p: 'XX' },
              { n: '高扭矩电机', img: '/images/shop_motor_high_torque.webp', p: 'XX' },
              { n: '18650电池', img: '/images/shop_battery_18650.webp', p: 'XX' },
            ].map((item) => (
              <Link key={item.n} to="/shop" className="bg-panel-dark/35 border border-border-dark rounded-card mecha-card mecha-bolt p-4 hover:border-arena-blue/40 transition-all group text-center">
                <img src={item.img} alt={item.n} className="h-16 w-auto mx-auto object-contain mb-3 group-hover:scale-105 transition-transform duration-300" />
                <p className="text-xs text-text-primary truncate">{item.n}</p>
                <p className="text-sm text-arena-gold font-medium mt-1">{item.p}</p>
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
            <Link to="/models" className="flex items-center gap-3 p-4 bg-panel-dark/35 border border-border-dark rounded-card mecha-card mecha-bolt hover:border-arena-blue/40 transition-all group">
              <div className="w-10 h-10 rounded-full bg-arena-gold/10 flex items-center justify-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFB800" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">3D 模型库</p>
                <p className="text-xs text-text-secondary">下载/打印/装车，佣金15%-20%</p>
              </div>
            </Link>
            <Link to="/rankings" className="flex items-center gap-3 p-4 bg-panel-dark/35 border border-border-dark rounded-card mecha-card mecha-bolt hover:border-arena-blue/40 transition-all group">
              <div className="w-10 h-10 rounded-full bg-arena-blue/10 flex items-center justify-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00C2FF" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">创造者社区</p>
                <p className="text-xs text-text-secondary">上传作品，被采纳即获佣金</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
